/**
 * 25 Oct 2026 (D-1025-EVENTS, proposal §3.8 and the 17 Oct Events redesign, "Wheelchair rule").
 *
 * An event can set aside a number of wheelchair spots (an outing's bus, a theatre's accessible row). The number lives in
 * the event's own file (`cal.wheelchairSpots`); empty means the event does not count them (every older event).
 *
 *   A rider            a person who needs a wheelchair spot: the row's own "Wheelchair" tag counts one; the office can add
 *                      the guests who need one too (`wheelchairGuests`, kept in the event's file per RSVP line).
 *   The pre-pass       before the places are settled, riders are given a wheelchair spot in the order they answered. A
 *                      rider who already holds a place keeps it (a yes is never taken back by someone else's answer).
 *                      Riders past the spots wait, and the page says so. A wheelchair spot is inside the event's limit
 *                      (40 places with 2 of them wheelchair spots), it is not on top of it — Eli to confirm.
 *   Spots left         spots − riders who hold a place. Never below 0; `over` says how many riders hold a place past the spots.
 *
 * Pure: nothing here reads or writes a list. No dietary or allergy question exists anywhere near this.
 */
import type { Accessibility } from './events';

export const WHEELCHAIR_MAX = 40;

/** How many people on this line need a wheelchair spot: the tag counts one, the guests the office added count more; never more than the party. */
export function wheelchairRiders(accessibility: readonly Accessibility[], guests: number, attending: number): number {
  const tag = accessibility.includes('Wheelchair') ? 1 : 0;
  const extra = Math.max(0, Math.floor(guests) || 0);
  const riders = Math.min(tag + extra, WHEELCHAIR_MAX);
  return attending > 0 ? Math.min(riders, attending) : riders;
}

export interface WheelchairLine {
  id: string;
  riders: number;
  /** Holds a place now (guaranteed, winner or going). */
  holds: boolean;
  /** Waiting for a place (standby). */
  waiting?: boolean;
  /** Asked for one in a raffle that has not been drawn. */
  inDraw?: boolean;
}

export interface WheelchairState {
  /** The number set aside; null = this event does not count them. */
  spots: number | null;
  /** Riders who hold a place. */
  taken: number;
  /** Spots still free; null when no number is set. */
  left: number | null;
  /** Riders who hold a place past the spots (the number was lowered after they were given one). */
  over: number;
  /** Riders on the waiting list. */
  waiting: number;
  /** Riders in a raffle that has not been drawn. */
  inDraw: number;
}

export function wheelchairState(spots: number | null, lines: readonly WheelchairLine[]): WheelchairState {
  const sum = (xs: readonly WheelchairLine[]) => xs.reduce((n, l) => n + l.riders, 0);
  const taken = sum(lines.filter((l) => l.riders > 0 && l.holds));
  return {
    spots,
    taken,
    left: spots === null ? null : Math.max(0, spots - taken),
    over: spots === null ? 0 : Math.max(0, taken - spots),
    waiting: sum(lines.filter((l) => l.riders > 0 && !l.holds && l.waiting)),
    inDraw: sum(lines.filter((l) => l.riders > 0 && !l.holds && l.inDraw)),
  };
}

export interface PrePassLine {
  id: string;
  riders: number;
  /** Already holds a place: kept. */
  holding: boolean;
  /** When the first yes came (ties broken by id) — the order of answering. */
  order: string;
}

/**
 * The pre-pass of `settle`: who among the riders is given a wheelchair spot and who waits. `spots` null = not counted:
 * nobody is seated first and nobody waits for it. Holders keep their place whatever the number now is; the rest take the
 * spots that are left in the order they answered; a party with more riders than spots left waits (a smaller one behind it may
 * take what remains, as everywhere else).
 */
export function wheelchairPrePass(lines: readonly PrePassLine[], spots: number | null): { seated: Set<string>; waiting: string[] } {
  const seated = new Set<string>();
  const waiting: string[] = [];
  if (spots === null) return { seated, waiting };
  const riders = lines.filter((l) => l.riders > 0);
  let used = 0;
  for (const l of riders) {
    if (l.holding) {
      seated.add(l.id);
      used += l.riders;
    }
  }
  const rest = riders.filter((l) => !l.holding).sort((a, b) => a.order.localeCompare(b.order) || a.id.localeCompare(b.id));
  for (const l of rest) {
    if (used + l.riders <= spots) {
      seated.add(l.id);
      used += l.riders;
    } else waiting.push(l.id);
  }
  return { seated, waiting };
}

/** The one sentence the page and the desk say: "Wheelchair spots: 1 left of 2." / "… none left — the next rider waits." / null when not counted. */
export type WheelchairWords = { kind: 'off' } | { kind: 'left'; left: number; spots: number } | { kind: 'none'; spots: number } | { kind: 'over'; over: number; spots: number };
export function wheelchairWords(s: WheelchairState): WheelchairWords {
  if (s.spots === null) return { kind: 'off' };
  if (s.over > 0) return { kind: 'over', over: s.over, spots: s.spots };
  if (s.left === 0) return { kind: 'none', spots: s.spots };
  return { kind: 'left', left: s.left ?? 0, spots: s.spots };
}

/** A number typed in Edit: blank = not counted (null); 0–40 whole; anything else is not accepted (undefined). */
export function wheelchairSpotsOf(text: string): number | null | undefined {
  const s = text.trim();
  if (!s) return null;
  if (!/^\d{1,2}$/.test(s)) return undefined;
  const n = Number(s);
  return n <= WHEELCHAIR_MAX ? n : undefined;
}
